import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { BedDouble, MapPin, ShieldCheck, Star, Filter as FilterIcon, Search as SearchIcon } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";

const HOUSING_TYPES = [
  { value: "all", label: "All Types" },
  { value: "veteran", label: "Veteran Housing" },
  { value: "recovery", label: "Recovery / Sober Living" },
  { value: "transitional", label: "Transitional" },
  { value: "reentry", label: "Reentry" },
  { value: "ilf", label: "Independent Living" },
  { value: "family", label: "Family Housing" },
  { value: "senior", label: "Senior Housing" },
  { value: "emergency", label: "Emergency" },
  { value: "shelter", label: "Shelter" },
];

const POPULATIONS = [
  { value: "all", label: "All Populations" },
  { value: "veterans", label: "Veterans" },
  { value: "seniors", label: "Seniors" },
  { value: "men", label: "Men" },
  { value: "women", label: "Women" },
  { value: "couples", label: "Couples" },
  { value: "families", label: "Families" },
  { value: "recovery", label: "Recovery" },
  { value: "reentry", label: "Reentry" },
  { value: "disabled", label: "Disabled" },
  { value: "general", label: "General Population" },
];

const defaultImg = "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?crop=entropy&cs=srgb&fm=jpg&q=85&w=800";

export default function Search() {
  const [filters, setFilters] = useState({ city: "", county: "", state: "", housing_type: "all", population: "all", veteran_only: false, available_only: false });
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchListings = async () => {
    setLoading(true);
    const params = {};
    if (filters.city) params.city = filters.city;
    if (filters.county) params.county = filters.county;
    if (filters.state) params.state = filters.state;
    if (filters.housing_type !== "all") params.housing_type = filters.housing_type;
    if (filters.population !== "all") params.population = filters.population;
    if (filters.veteran_only) params.veteran_only = true;
    if (filters.available_only) params.available_only = true;
    try {
      const { data } = await api.get("/listings", { params });
      setListings(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchListings(); /* eslint-disable-next-line */ }, []);

  return (
    <div data-testid="search-page" className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
      <div className="mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[#C05621] font-semibold mb-2">Browse Housing</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F]">Find available beds in real time</h1>
        <p className="mt-2 text-[#4A5568]">Search by state, county, city, type, or population served — built for all 50 states.</p>
      </div>

      <div className="bg-white border border-[#D1D5DB] p-5 rounded-sm mb-8">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-3 items-end">
          <div className="col-span-2 md:col-span-2">
            <label className="text-xs text-[#4A5568] uppercase tracking-wider">State</label>
            <Input data-testid="filter-state" value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value.toUpperCase() })} placeholder="NC" maxLength={2} className="border-[#D1D5DB]" />
          </div>
          <div className="col-span-2 md:col-span-2">
            <label className="text-xs text-[#4A5568] uppercase tracking-wider">County</label>
            <Input data-testid="filter-county" value={filters.county} onChange={(e) => setFilters({ ...filters, county: e.target.value })} placeholder="Mecklenburg" className="border-[#D1D5DB]" />
          </div>
          <div className="col-span-2 md:col-span-2">
            <label className="text-xs text-[#4A5568] uppercase tracking-wider">City</label>
            <Input data-testid="filter-city" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })} placeholder="Charlotte" className="border-[#D1D5DB]" />
          </div>
          <div className="col-span-2 md:col-span-2">
            <label className="text-xs text-[#4A5568] uppercase tracking-wider">Type</label>
            <Select value={filters.housing_type} onValueChange={(v) => setFilters({ ...filters, housing_type: v })}>
              <SelectTrigger data-testid="filter-type" className="border-[#D1D5DB]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {HOUSING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-2">
            <label className="text-xs text-[#4A5568] uppercase tracking-wider">Population</label>
            <Select value={filters.population} onValueChange={(v) => setFilters({ ...filters, population: v })}>
              <SelectTrigger data-testid="filter-population" className="border-[#D1D5DB]"><SelectValue /></SelectTrigger>
              <SelectContent>
                {POPULATIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 md:col-span-2">
            <Button data-testid="search-submit" onClick={fetchListings} className="w-full bg-[#1E3A8A] hover:bg-[#1A365D] text-white">
              <SearchIcon className="w-4 h-4 mr-2" /> Search
            </Button>
          </div>
          <div className="col-span-2 md:col-span-12 flex gap-6 pt-2 border-t border-[#D1D5DB]">
            <label className="flex items-center gap-2 text-sm">
              <Checkbox data-testid="filter-veteran" checked={filters.veteran_only} onCheckedChange={(v) => setFilters({ ...filters, veteran_only: !!v })} />
              Veteran-only
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox data-testid="filter-available" checked={filters.available_only} onCheckedChange={(v) => setFilters({ ...filters, available_only: !!v })} />
              Available now
            </label>
          </div>
        </div>
      </div>

      <div className="text-sm text-[#4A5568] mb-4">
        {loading ? "Searching…" : `${listings.length} result${listings.length !== 1 ? "s" : ""}`}
      </div>

      {!loading && listings.length === 0 && (
        <div className="border border-dashed border-[#D1D5DB] p-12 text-center bg-white rounded-sm">
          <FilterIcon className="w-8 h-8 mx-auto text-[#4A5568] mb-3" />
          <div className="font-heading font-semibold text-[#0A192F]">No matching housing yet</div>
          <div className="text-sm text-[#4A5568] mt-1">Try removing some filters, or check back soon — providers post vacancies in real time.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {listings.map((l) => (
          <Link key={l.id} to={`/listings/${l.id}`} data-testid={`listing-${l.id}`}
            className="bg-white border border-[#D1D5DB] rounded-sm overflow-hidden shv-card-hover block">
            <div className="relative h-44 bg-[#F5F5F0]">
              <img src={l.photo_url || defaultImg} alt={l.name} className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3 flex gap-1.5">
                {l.provider_featured && <Badge className="bg-[#FEEBC8] text-[#7B341E] hover:bg-[#FEEBC8] border-0"><Star className="w-3 h-3 mr-1" /> Featured</Badge>}
                {l.provider_verified && <Badge className="bg-[#C6F6D5] text-[#22543D] hover:bg-[#C6F6D5] border-0"><ShieldCheck className="w-3 h-3 mr-1" /> Verified</Badge>}
              </div>
              <div className="absolute top-3 right-3 bg-white px-2.5 py-1 text-xs font-bold border border-[#D1D5DB] flex items-center gap-1">
                <BedDouble className="w-3.5 h-3.5 text-[#C05621]" /> {l.available_beds}/{l.total_beds}
              </div>
            </div>
            <div className="p-5">
              <div className="text-xs uppercase tracking-wider text-[#C05621] font-semibold">{l.housing_type.replace('_', ' ')}</div>
              <h3 className="font-heading font-semibold text-[#0A192F] text-lg mt-1">{l.name}</h3>
              <div className="flex items-center gap-1.5 text-xs text-[#4A5568] mt-1.5">
                <MapPin className="w-3.5 h-3.5" /> {l.city}{l.county && `, ${l.county} Co.`}, {l.state}
              </div>
              {l.populations_served?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {l.populations_served.slice(0, 3).map((p) => <Badge key={p} variant="outline" className="text-[10px] capitalize">{p}</Badge>)}
                  {l.populations_served.length > 3 && <span className="text-[10px] text-[#4A5568]">+{l.populations_served.length - 3}</span>}
                </div>
              )}
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#D1D5DB]">
                <span className="text-sm text-[#4A5568]">{l.veteran_only ? "Veterans" : "Open eligibility"}</span>
                <span className="text-sm font-semibold text-[#0A192F]">{l.monthly_rent > 0 ? `$${l.monthly_rent.toFixed(0)}/mo` : "Free"}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
